import { IconName } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import parse from 'html-react-parser';
import { Key, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import Button from '../components/Button';
import FullscreenLoader from '../components/FullscreenLoader';
import Logo from '../components/Logo';
import ModalPage from '../components/ModalPage';
import { useAuthContext } from '../providers/AuthProvider';
import { useNewApplicationContext } from '../providers/NewApplicationProvider';
import { useConfigContext } from '../providers/ConfigProvider';
import { clearActiveDraft } from '../helpers/activeDraft';
import Summary from './Summary';
import { z } from 'zod';
import { FormType, ModalType, SectionType } from '../helpers/types';
import { tipsIconUrl } from '../helpers/utils';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import KineContainer from '../components/Kine';
import useKine from '../hooks/useKine'; 
import { keepOnMainThread } from '../helpers/motion';
import { flattenSchema } from '../helpers/schema';
import { sectionPath } from '../helpers/sectionPath';
import { PLAYGROUND_DONE, PLAYGROUND_HOME } from '../helpers/playground';

const Section = () => {
    const { t } = useTranslation();
    const { 
        application, 
        setApplicationData, 
        section, 
        form, formData, 
        consentApproved, 
        setConsentApproved, 
        allValid
    , saveDraft, sandbox, basePath } = useNewApplicationContext();

     const { consentKine, assistantAvailable } = useKine();

    const params = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (application === null) {
            setApplicationData(params.formType ?? '');
        }
    });

    if (!application || !section) {
        return <FullscreenLoader />;
    }

    if (section.type === 'pre') {
        return (
            <motion.div key="pre" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} onUpdate={keepOnMainThread} className="container d-flex align-items-lg-center justify-content-center h-100">
                <div className="row justify-content-center align-items-center w-100">
                    <div className="col-md-10 col-lg-8 col-xl-8">
                        <div className="text-center py-5">
                            <span className="fw-medium" style={{ fontSize: 20 }}>
                                {section.title}
                            </span>
                            <Outlet />
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    }

    if (section.type === 'modal') {
        return <Modal {...section} />;
    }

    if (section.type === 'form' || section.type === 'user_info' || section.type === 'summary') {
        return (
            <motion.div key={section.url} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} onUpdate={keepOnMainThread} className="container">
                <div className="row">
                    <div className="col-lg-4 d-none d-lg-flex flex-column align-items-end py-5 sticky-sidebar" style={{ paddingRight: '64px', zIndex: 3, minHeight: '100vh' }}>
                        <Logo />
                        <div className="sidebar-navigation mt-5">
                            {application.sections.map((item: SectionType, index: Key) => {
                                if (item.type === 'form' || item.type === 'user_info') {
                                    return <SidebarSection key={index} item={item} />;
                                }
                            })}

                            {allValid && (
                                <SidebarSection
                                    item={{
                                        title: 'Oppsummering',
                                        icon: 'line-columns',
                                        short_description: '',
                                        url: 'oppsummering',
                                        type: 'form',
                                        forms: [],
                                    }}
                                />
                            )}
                        </div>
                        <a
                            href="#"
                            className="text-decoration-none d-flex align-items-center mt-auto"
                            onClick={async (e) => {
                                e.preventDefault();
                                // Bli værende hvis lagringen feilet — saveDraft har
                                // da allerede vist feiltoasten.
                                const saved = await saveDraft();
                                if (saved) navigate(sandbox ? PLAYGROUND_HOME : '/din-side');
                            }}
                        >
                            {t(sandbox ? 'playground_close' : 'end_and_continue_later')}
                            <FontAwesomeIcon icon={['fal', 'xmark']} className="text-primary ms-1" />
                        </a>
                    </div>

                    <form className="steps-right-side col-lg-8 pt-2 pt-lg-5  position-relative needs-validation d-flex flex-column" noValidate>
                        <ProgressBar />
                        <div className="flex-grow-1">
                        {
                            section.type === 'summary' ? <Summary section={section} formData={formData} application={application} consentApproved={consentApproved} setConsentApproved={setConsentApproved} type="application" editBasePath={basePath} />
                            : <Outlet />}
                        
                        </div>
                        { consentKine && assistantAvailable ? <KineContainer /> : <Tips />}
                        <Footer formData={formData} />
                    </form>
                </div>
            </motion.div>
        );
    }

    return <p>{t('404')}</p>;
};

const Modal = (props: ModalType) => {
    const { title, subtitle, content, button, image, video, modalType, buttons } = props;
    const { prev, next, application, section, sandbox } = useNewApplicationContext();
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Når modalen er aller første skjerm finnes ingen forrige skjerm å gå til.
    // Da skal knappen hete "Tilbake" og ta brukeren tilbake til Din side.
    const isFirstSection = application?.sections?.[0]?.url === section?.url;
    const backButton = isFirstSection ? (
        <Button title="Lukk skjema" type="linkbtn" leftIcon="arrow-left" onClick={() => navigate(sandbox ? PLAYGROUND_HOME : '/din-side')} />
    ) : (
        <Button title={t('previous_screen')} type="linkbtn" leftIcon="arrow-left" onClick={prev} />
    );

    const onClickButton = (onclick) => {

        switch(onclick?.type){
            case 'url':
                return () => window.open(onclick.value, '_blank');
            case 'back':
                return prev;
            case 'next':
                return next;
            default: 
                return next;
        }
    }

    if (modalType === 'middleSection') {
        return (
            <ModalPage modalType={modalType} showCheck={true}>
                <h2>{title}</h2>
                <p className="my-3">{parse(content)}</p>

                {video && <video className="w-100 rounded-2" src={video} controls></video>}

                <div className="text-center my-3">
                    {buttons && buttons.length > 0 ? 
                        buttons.map((btn, index) => 
                            <Button 
                                key={index} 
                                title={btn.text} 
                                type={btn.type} 
                                classes="col-auto" 
                                rightIcon={btn.icons?.right} 
                                onClick={onClickButton(btn.onclick)} />)
                    : 
                        button ? (
                            <Button title={button.text} type={button.type} classes="col-auto" rightIcon="arrow-right" onClick={next} />
                        ) :  <Button title="Gå videre" classes="col-auto" rightIcon="arrow-right" onClick={next} />
                    }
                </div>
                <div className="text-center">
                    {backButton}
                </div>
            </ModalPage>
        );
    }

    return (
            <ModalPage image={image} video={video} modalType={modalType}>
                <h2>{title}</h2>
                {subtitle && <h4 className="text-muted modal-subtitle">{subtitle}</h4>}
                <p>{parse(content)}</p>

                <div className="row justify-content-center gap-3">
                   
                    {buttons && buttons.length > 0 ? 
                        buttons.map((btn, index) => 
                            <Button 
                                key={index} 
                                title={btn.text} 
                                type={btn.type} 
                                classes="col-auto" 
                                rightIcon={btn.icons?.right} 
                                onClick={onClickButton(btn.onclick)} />)
                    : 
                        button ? (
                            <Button title={button.text} type={button.type} classes="col-auto" rightIcon="arrow-right" onClick={next} />
                        ) :  <Button title="Gå videre" classes="col-auto" rightIcon="arrow-right" onClick={next} />
                    }
                </div>
                <div className="row justify-content-center">
                    {backButton}
                </div>
            </ModalPage>
        );

    return <div>{t('404')}</div>;
};

const SidebarSection = ({ item }: { item: SectionType }) => {
    const { formData, schema, application, section, saveDraft, checkForIncompleteSection, basePath } = useNewApplicationContext();
    const { title, icon, short_description, url, type } = item;
    const [circleIcon, setCircleIcon] = useState(icon ?? 'circle');
    const [classess, setClassess] = useState({
        clickarea: 'clickarea-default',
    });

    const navigate = useNavigate();
    const params = useParams();
    const sectionSchema = schema?.shape[item.url];
    const flatSectionSchema = useMemo(() => flattenSchema(sectionSchema), [sectionSchema]);
    const flatZodSchema = useMemo(() => z.object(flatSectionSchema), [flatSectionSchema]);

    const isCurrentOrPreviousSection = (sectionUrl: string) => {
        const sectionIndex = application.sections.findIndex((s: SectionType) => s.url === sectionUrl);
        const currentSectionIndex = application.sections.findIndex((s: SectionType) => s.url === params.section);
        return sectionIndex <= currentSectionIndex;
    };

    useEffect(() => {
        const validateSchema = () => {
            if (!sectionSchema) return;

            // Validér per-form for å bevare form-nivå superRefines
            // (spesielt "Begrenset visning"-required-håndhevingen). Hopp over
            // sections som ikke har noen forms.
            const formKeys = Object.keys((sectionSchema as any).shape ?? {});
            if (formKeys.length === 0) return;

            let success = true;
            for (const formKey of formKeys) {
                const formSchema = (sectionSchema as any).shape[formKey];
                if (!formSchema) continue;
                const res = formSchema.safeParse(formData);
                if (!res.success) {
                    success = false;
                    break;
                }
            }

            if (success) {
                if (params.section !== url && isCurrentOrPreviousSection(url)) {
                    setCircleIcon('check');
                }
                if (isCurrentOrPreviousSection(url)) {
                    setClassess({
                        clickarea: 'clickarea-completed',
                    });
                }
            } else {
                setCircleIcon(icon ?? 'circle');
                setClassess({
                    clickarea: 'clickarea-default',
                });
            }
        };

        validateSchema();
    }, [formData]);

    const handleClick = async () => {
        if (!isCurrentOrPreviousSection(url)) {
            // Check if the section is in progress
            const progressUrl = await checkForIncompleteSection(url);
            if (progressUrl) {
                await saveDraft();
                navigate(`${basePath}/${application.url}/${progressUrl}`);
                return;
            } else {
                return null;
            }
        }

        const currentIndex = application.sections.findIndex((s: SectionType) => s.url === section?.url);
        const clickedIndex = application.sections.findIndex((s: SectionType) => s.url === url);

        // Always allow navigation to a previous section
        if (clickedIndex < currentIndex) {
            await saveDraft();
            navigateToSection(clickedIndex);
            return true;
        }

        // Validate form data
        const { success } = flatZodSchema.safeParse(formData);

        if (success) {
            await saveDraft();
            navigateToSection(clickedIndex);
        }
    };

    const navigateToSection = (index: number) => {
        const targetSection = application.sections[index];
        if (type === 'summary') {
            navigate(`${basePath}/${application.url}/${url}`);
        } else if (targetSection) {
            navigate(`${basePath}/${application.url}/${sectionPath(targetSection)}`);
        }
    };

    return (
        <div className="sidebar-navigation-item position-relative" style={{ paddingBottom: '64px' }}>
            <div className={`clickarea  ${classess['clickarea']} ${params.section === url ? 'clickarea-active' : ''}  d-flex justify-content-end align-items-center`} style={{ cursor: 'pointer' }} onClick={handleClick}>
                <div className="sidebar-navigation-item-content text-end">
                    <h6 className="mb-0">{title}</h6>
                    <span className="small">{short_description}</span>
                </div>
                <div className="clickarea-circle d-flex justify-content-center align-items-center border rounded-pill ms-3 " style={{ width: '72px', height: '72px', aspectRatio: 1 }}>
                    <FontAwesomeIcon icon={['fal', circleIcon as IconName]} size="lg" />
                </div>
            </div>
        </div>
    );
};

const ProgressBar = () => {
    const { t } = useTranslation();
    const { application, formData, schema, form} = useNewApplicationContext();

    const [totalProgress, setTotalProgress] = useState(0);
    const [currentProgress, setCurrentProgress] = useState(0);
    const [modalActive, setModalActive] = useState(false);

    const customProgress = application.custom_progress;
    const navigate = useNavigate();
    const params = useParams();

    const isPreviousSection = (sectionUrl: string) => {
        const sectionIndex = application.sections.findIndex((s: SectionType) => s.url === sectionUrl);
        const currentSectionIndex = application.sections.findIndex((s: SectionType) => s.url === params.section);
        return sectionIndex < currentSectionIndex;
    };

    const isPreviousForm = (sectionUrl: string, formUrl: string) => {
        const sectionIndex = application.sections.findIndex((s: SectionType) => s.url === sectionUrl);
        const formIndex = application.sections[sectionIndex].forms.findIndex((f: FormType) => f.url === formUrl);
        const currentFormIndex = application.sections[sectionIndex].forms.findIndex((f: FormType) => f.url === params.form);
        return formIndex <= currentFormIndex;
    };

    const updateProgress = () => {
        if (!schema) return;

        let total = 0;
        let current = 0;

        // Loop through schema and check progress
        for (const sectionKey of Object.keys(schema.shape)) {
            const sectionObject = schema.shape[sectionKey];

            for (const formKey of Object.keys(sectionObject.shape)) {
                const formObject = sectionObject.shape[formKey];

                total++;

                // Wait for the result of safeParseAsync
                const response = formObject.safeParse(formData);
                if (response.success) {
                    if (customProgress) {
                        if (isPreviousSection(sectionKey)) {
                            current++;
                        } else {
                            if (isPreviousForm(sectionKey, formKey)) {
                                current++;
                            }
                        }
                    } else {
                        current++;
                    }
                }
            }
        }

        setTotalProgress(total);
        setCurrentProgress(current);
    };

    useEffect(() => {
        updateProgress();
    }, [formData, form]);

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-medium" style={{ fontSize: '20px' }}>
                    {application.title}
                </span>
            </div>
            <div className="progress" role="progressbar" aria-label="Basic example" aria-valuenow={(currentProgress / totalProgress) * 100} aria-valuemin={0} aria-valuemax={100}>
                <div className="progress-bar bg-success" style={{ width: (currentProgress / totalProgress) * 100 + '%' }}></div>
            </div>
        </>
    );
};

const Tips = () => {
    const { form } = useNewApplicationContext();

    if (form && form.tips) {
        const { icon, title, value, link } = form.tips;

        return (
            <div className="row mt-5 align-items-start align-items-md-center">
                {/* «Ingen ikon» i form-builder → ingen kolonne, ikke en tom kolonne. */}
                {tipsIconUrl(icon) && (
                    <div className="col-3 col-md-auto mb-4">
                        <img src={tipsIconUrl(icon)!} alt="" style={{ maxWidth: '135px', width: '100%' }} />
                    </div>
                )}
                <div className="col">
                    {title && <h4>{title}</h4>}
                    {value && <div className="small d-md-none">{parse(value)}</div>}
                    {value && <div className="d-none d-md-block">{parse(value)}</div>}
                    {link && link.title && link.url && (
                        <p>
                            <a href={link.url} target="_blank">
                                {link.title}
                            </a>
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return null;
};

const Footer = ({ formData }: { formData: Record<string, string> }) => {
    const { apiCall } = useAuthContext();
    const { t } = useTranslation();
    const { prev, next, saveDraft, schema, section, form, application, consentApproved, applicationId, allValid, setAllValid, debugBypass, sandbox } = useNewApplicationContext();
    const { setDraftId } = useConfigContext();
    const [valid, setValid] = useState(false);
    const [isSending, setSending] = useState(false);
    const [isSaving, setSaving] = useState(false);
    const [error, setError] = useState(undefined);
    const navigate = useNavigate();

    const currentValidation = () => {
        if (form && section) {
            const sectionSchema = schema?.shape[section.url];
            const formSchema = sectionSchema?.shape[form.url];

            if (sectionSchema && formSchema) {
                const response = formSchema.safeParse(formData);
                if (response.success) {
                    setValid(true);
                } else {
                    setValid(false);
                }
            }
        }
    };

    const validateSchema = () => {
        if (!schema || !application) return;

        // Validér per-form i stedet for å flate ut schemaet. `flattenSchema`
        // mister form-nivå superRefines — spesielt den nye som re-aktiverer
        // `required` for "Begrenset visning"-felter når kriteriene matcher.
        // Per-form-validering bevarer dem, så et tomt restricted+required-
        // felt som ER synlig blir fanget opp før "Send inn" aktiveres.
        let isValid = true;
        for (const sec of application.sections) {
            if (!sec.forms || sec.forms.length === 0) continue;
            const sectionSchema = (schema as any).shape?.[sec.url];
            if (!sectionSchema) continue;
            for (const f of sec.forms) {
                const formSchema = sectionSchema.shape?.[f.url];
                if (!formSchema) continue;
                const res = formSchema.safeParse(formData);
                if (!res.success) {
                    isValid = false;
                    break;
                }
            }
            if (!isValid) break;
        }

        setAllValid(isValid);
    };

    const handleCompleteApplication = async () => {
        if (sandbox) {
            navigate(PLAYGROUND_DONE, {
                state: { title: application?.title, retry: `/lekeplass/soknad/${application?.url}` },
            });
            return;
        }

        setSending(true);
        setError(undefined);
        try {
            const response = await apiCall({
                action: 'completeApplication',
                applicationId,
            });

            if (response?.data?.status === 200) {
                if (application?.url) clearActiveDraft(application.url);
                setDraftId(null);
                navigate('/soknadskjema/soknad-innsendt');
            } else {
                setError(response?.data?.message);
            }
        } catch (error) {
            console.error('Error completing application:', error);
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        currentValidation();
    }, [formData]);

    useEffect(() => {
        currentValidation();
        validateSchema();
    }, [form]);

    return (
        <div className="position-fixed w-100 no-print" style={{ left: 0, bottom: 0, zIndex: 2, maxWidth: 'inherit' }}>
            <div className="container">
                <div className="row justify-content-end">
                    <div className="col-lg-8">
                        <div className={`d-flex ${section?.type === 'summary' ? 'justify-content-center justify-content-lg-end' : 'justify-content-between'} align-items-center pb-lg-5 pt-lg-4 py-3 border-top blur-plate`}>
                            {section?.type === 'summary' ? (
                                <>
                                    {error && <span className="me-auto pe-4 text-danger">{error}</span>}
                                    {sandbox && (!consentApproved || !allValid) && (
                                        <Button title={t('playground_skip_validation')} type="linkbtn" classes="me-3" onClick={handleCompleteApplication} />
                                    )}
                                    <Button title={t('application_send')} rightIcon="arrow-right" onClick={handleCompleteApplication} disabled={!consentApproved || (!allValid && !debugBypass)} loading={isSending} />
                                </>
                            ) : (
                                <>
                                    <div className="mx-4 d-flex justify-content-between w-100">
                                        <Button title={t('previous')} leftIcon="arrow-left" type="outline-primary" onClick={prev} />
                                        {(valid || debugBypass) ? (
                                            <Button
                                                title={t('next')}
                                                rightIcon="arrow-right"
                                                type="primary"
                                                disabled={isSaving}
                                                loading={isSaving}
                                                onClick={async () => {
                                                    setSaving(true);
                                                    try {
                                                        const saved = await saveDraft();
                                                        if (saved) next();
                                                    } finally {
                                                        setSaving(false);
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <div className="d-flex align-items-center">
                                                {/* Lekeplassen: saveDraft er en no-op, så next() er alt som trengs. */}
                                                {sandbox && <Button title={t('playground_skip_validation')} type="linkbtn" classes="me-3" onClick={next} />}
                                                <Button title={t('next')} rightIcon="arrow-right" style={{ opacity: 0.5 }} onClick={(e) => {
                                                    e.preventDefault();
                                                }} />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Section;
